import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import AdminLogin from "./AdminLogin";
import { LayoutDashboard, FileText, Users, LogOut, Newspaper, Home, Lock, Inbox } from "lucide-react";

const STAFF = ["admin", "editor", "journalist"];

const ROLE_LABEL = { admin: "Administrateur", editor: "Éditeur", journalist: "Journaliste", reader: "Lecteur" };

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]"><div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!user) return <AdminLogin />;

  if (!STAFF.includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-4">
        <div className="max-w-md text-center bg-white p-10 rounded-2xl border border-[#E2E8F0]">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4"><Lock className="text-amber-600" size={24} /></div>
          <h1 className="font-headline text-2xl font-black text-[#0F172A] mb-2">Accès en attente</h1>
          <p className="font-body text-slate-500 mb-6">Bonjour {user.name}, votre compte n'a pas encore de rôle rédactionnel. Contactez un administrateur pour obtenir l'accès.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate("/")} className="meta-label text-slate-500 px-4 py-2">Accueil</button>
            <button onClick={async () => { await logout(); }} data-testid="noaccess-logout" className="meta-label bg-[#0F172A] text-white px-4 py-2 rounded-full">Se déconnecter</button>
          </div>
        </div>
      </div>
    );
  }

  const nav = [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, exact: true },
    { to: "/admin/articles/new", label: "Nouvel article", icon: FileText },
    ...(user.role === "admin" ? [{ to: "/admin/users", label: "Utilisateurs", icon: Users }, { to: "/admin/messages", label: "Messages", icon: Inbox }] : []),
  ];

  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex">
      <aside className="w-60 bg-[#0F172A] text-white flex flex-col fixed h-full z-30 hidden md:flex">
        <div className="p-5 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2">
            <Newspaper size={20} className="text-[#E11D48]" />
            <span className="font-headline text-lg font-black">Le Véridique <span className="text-[#E11D48]">Admin</span></span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((item) => (
            <Link key={item.to} to={item.to} data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-sub text-sm transition-colors ${isActive(item) ? "bg-[#E11D48] text-white" : "text-slate-300 hover:bg-white/10"}`}>
              <item.icon size={17} /> {item.label}
            </Link>
          ))}
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg font-sub text-sm text-slate-300 hover:bg-white/10">
            <Home size={17} /> Voir le site
          </Link>
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <div className="w-9 h-9 rounded-full bg-[#E11D48] flex items-center justify-center font-headline font-bold">{user.name.charAt(0).toUpperCase()}</div>
            <div className="min-w-0">
              <p className="font-sub text-sm font-semibold truncate">{user.name}</p>
              <p className="meta-label text-slate-400">{ROLE_LABEL[user.role]}</p>
            </div>
          </div>
          <button onClick={async () => { await logout(); }} data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-sub text-sm text-slate-300 hover:bg-white/10">
            <LogOut size={17} /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-60 min-w-0">
        {/* mobile top bar */}
        <div className="md:hidden bg-[#0F172A] text-white flex items-center justify-between px-4 py-3 sticky top-0 z-20">
          <Link to="/admin" className="font-headline font-black">Le Véridique Admin</Link>
          <div className="flex items-center gap-3">
            <Link to="/admin/articles/new" className="meta-label bg-[#E11D48] px-3 py-1.5 rounded">Nouveau</Link>
            <button onClick={async () => { await logout(); }} data-testid="admin-logout-mobile"><LogOut size={18} /></button>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
